import React, { useState, useEffect } from 'react';
import { Elements, useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import ButtonSymphony from 'elements/ButtonSymphony';
import { showToast } from 'modules/Utils';
import { createSetupIntent_api, confirmSetupIntent_api } from 'api/paymentsV2Api';
import Loader from 'elements/Loader';

const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = loadStripe(stripePublishableKey || '');

interface SetupPaymentMethodProps {
    organizationId: number;
    onSuccess: () => void;
    onError?: (error: string) => void;
    onCancel?: () => void;
    submitButtonText?: string;
    showCancelButton?: boolean;
}

interface SetupFormProps {
    organizationId: number;
    onSuccess: () => void;
    onError?: (error: string) => void;
    onCancel?: () => void;
    submitButtonText: string;
    showCancelButton: boolean;
}

const SetupForm: React.FC<SetupFormProps> = ({
    organizationId,
    onSuccess,
    onError,
    onCancel,
    submitButtonText,
    showCancelButton
}) => {
    const stripe = useStripe();
    const elements = useElements();
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleError = (error: { message?: string }) => {
        setLoading(false);
        const errorMessage = error.message || 'An error occurred';
        setErrorMessage(errorMessage);
        onError?.(errorMessage);
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setLoading(true);
        setErrorMessage(null);

        try {
            // Trigger form validation and wallet collection
            const { error: submitError } = await elements.submit();
            if (submitError) {
                handleError({ message: submitError.message });
                return;
            }

            // Create the SetupIntent and obtain clientSecret
            const { clientSecret } = await createSetupIntent_api(organizationId);

            // Confirm the SetupIntent using the details collected by the Payment Element
            const { error } = await stripe.confirmSetup({
                elements,
                clientSecret,
                confirmParams: {
                    return_url: window.location.href,
                },
                redirect: 'if_required'
            });

            if (error) {
                handleError({ message: error.message });
            } else {
                // Setup succeeded, now confirm with backend using dedicated endpoint
                const setupIntentId = clientSecret.split('_secret_')[0]; // Extract setup intent ID
                await confirmSetupIntent_api(setupIntentId);

                showToast({
                    message: 'Payment method added successfully!',
                    error: false,
                });
                onSuccess();
            }
        } catch (err: any) {
            const errorMessage = err.message || 'Failed to add payment method';
            handleError({ message: errorMessage });
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <PaymentElement />
            {errorMessage && (
                <div style={{ color: 'red', marginTop: '10px', fontSize: '14px' }}>
                    {errorMessage}
                </div>
            )}
            <ButtonSymphony
                className="submitButton"
                type="submit"
                disabled={!stripe || loading}
                isLoading={loading}
                style={{ width: '100%', marginTop: '20px' }}
                variant="primary"
            >
                {loading ? 'Processing...' : submitButtonText}
            </ButtonSymphony>
            {showCancelButton && onCancel && (
                <ButtonSymphony
                    className="cancelButton"
                    variant="text"
                    style={{ width: '100%', marginTop: '10px' }}
                    onClick={onCancel}
                >
                    Cancel
                </ButtonSymphony>
            )}
        </form>
    );
};

const SetupPaymentMethod: React.FC<SetupPaymentMethodProps> = ({
    organizationId,
    onSuccess,
    onError,
    onCancel,
    submitButtonText = 'Add Payment Method',
    showCancelButton = true
}) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        // Validate that we have an organization ID
        if (!organizationId) {
            setError('Organization ID is required');
            setLoading(false);
            return;
        }
        setLoading(false);
    }, [organizationId]);

    if (loading) {
        return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
                <Loader size={40} />
                <p style={{ marginTop: '10px' }}>Loading payment form...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: '20px', color: 'red', textAlign: 'center' }}>
                <p>{error}</p>
                {onCancel && (
                    <ButtonSymphony 
                        className="closeButton"
                        onClick={onCancel} 
                        style={{ marginTop: '15px' }}
                    >
                        Close
                    </ButtonSymphony>
                )}
            </div>
        );
    }

    const options = {
        mode: 'setup' as const,
        currency: 'usd',
        appearance: {
            theme: 'stripe' as const,
        },
    };

    return (
        <Elements stripe={stripePromise} options={options}>
            <SetupForm
                organizationId={organizationId}
                onSuccess={onSuccess}
                onError={onError}
                onCancel={onCancel}
                submitButtonText={submitButtonText}
                showCancelButton={showCancelButton}
            />
        </Elements>
    );
};

export default SetupPaymentMethod;
